/**
 * Factory helpers for new projects and objects.
 */

import { nanoid } from "nanoid";
import type { GuideCategory, GuideEntry, Item, Language, Project, Recipe } from "./types";
import { PROJECT_FORMAT_VERSION } from "./types";

export const DEFAULT_LANGUAGES: Language[] = [{ code: "en_US", label: "English (US)" }];

/** Turn a free-text pack name into a valid default namespace (Section 9.4). */
export function slugifyNamespace(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[^a-z]+/, ""); // must start with a letter
  return slug || "mypack";
}

function uuid(): string {
  // crypto.randomUUID is available in all Cloudflare/browser targets we ship to.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Deterministic-ish fallback (tests / non-secure contexts).
  return `${nanoid(8)}-${nanoid(4)}-${nanoid(4)}-${nanoid(4)}-${nanoid(12)}`;
}

export function newItem(id = "new_item"): Item {
  return {
    uuid: nanoid(),
    id,
    displayName: { en_US: id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) },
    category: "items",
  };
}

export function newRecipe(id = "new_recipe"): Recipe {
  return {
    uuid: nanoid(),
    id,
    shape: "shapeless",
    tags: ["crafting_table"],
    ingredients: [{ kind: "vanilla", id: "minecraft:stick" }],
    pattern: ["X"],
    key: { X: { kind: "vanilla", id: "minecraft:stick" } },
    result: { kind: "project", id },
    resultCount: 1,
  };
}

export function newGuideEntry(id = "new_entry"): GuideEntry {
  return {
    uuid: nanoid(),
    id,
    title: { en_US: id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) },
    body: { en_US: "Describe this item here." },
  };
}

export function newGuideCategory(id = "new_category"): GuideCategory {
  return {
    uuid: nanoid(),
    id,
    title: { en_US: id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) },
    body: { en_US: "" },
    entries: [newGuideEntry("first_entry")],
  };
}

export function newProject(packName = "My Guidebook"): Project {
  const namespace = slugifyNamespace(packName);
  return {
    formatVersion: PROJECT_FORMAT_VERSION,
    meta: {
      name: packName,
      description: "A guidebook add-on made with Guidebook Generator.",
      author: "",
    },
    namespace,
    guideItemId: "guide",
    languages: [...DEFAULT_LANGUAGES],
    items: [],
    recipes: [
      {
        uuid: nanoid(),
        id: "guide",
        shape: "shapeless",
        tags: ["crafting_table"],
        ingredients: [
          { kind: "vanilla", id: "minecraft:book" },
          { kind: "vanilla", id: "minecraft:feather" },
        ],
        pattern: ["X"],
        key: { X: { kind: "vanilla", id: "minecraft:book" } },
        // The guide recipe results in the guide item itself.
        result: { kind: "project", id: "guide" },
        resultCount: 1,
      },
    ],
    guide: {
      title: { en_US: "Guidebook" },
      intro: { en_US: "Craft a book + feather to open this guide. Pick a category below." },
      giveOnFirstJoin: true,
      categories: [
        {
          uuid: nanoid(),
          id: "getting_started",
          title: { en_US: "Getting Started" },
          body: { en_US: "" },
          entries: [
            {
              uuid: nanoid(),
              id: "welcome",
              title: { en_US: "Welcome" },
              body: { en_US: "This is your first guide entry. Add more categories and entries to build out your guidebook." },
            },
          ],
        },
      ],
    },
    manifest: {
      behaviorHeaderUuid: uuid(),
      behaviorModuleUuid: uuid(),
      behaviorScriptUuid: uuid(),
      resourceHeaderUuid: uuid(),
      resourceModuleUuid: uuid(),
      version: [1, 0, 0],
      // Custom Components v2 (system.beforeEvents.startup) is stable from 1.21.70
      // with @minecraft/server 2.x.
      minEngineVersion: [1, 21, 70],
      serverModuleVersion: "2.0.0",
      serverUiModuleVersion: "2.0.0",
    },
  };
}
